/**
 * features/applications/AdmitWizard.tsx
 * ------------------------------------------------------------------
 * Client-side admission wizard — replaces the single `admitApplication`
 * Cloud Function call with a stepped, deploy-free flow that runs
 * entirely on the client SDK. Launched from ApplicationDetail's "Admit
 * student" action (the approval gate — decisions, fees, family — has
 * already been satisfied before this opens).
 *
 * Four steps:
 *   1. Class               — confirm assignedClassId
 *                           (prefilled from app.decisions).
 *   2. Family             — its own searchable disambiguation screen,
 *                           because same-name families are otherwise
 *                           indistinguishable in a flat list. Shows the
 *                           primary contact + existing children per
 *                           candidate. Prefilled from app.familyLink.
 *   3. Charges            — joining-invoice lines prefilled via
 *                           joiningInvoiceLines() from financeCore;
 *                           every line is editable (label / category /
 *                           amount, add / remove).
 *   4. Review & submit    — student + family + invoice summary, then a
 *                           client-side submit.
 *
 * The submit mirrors the server `admitApplication` exactly:
 *   - admission number   — LKC + 6-digit sequence (getNextAdmissionNumber).
 *   - sibling discount    — mirrored client-side (effectiveDiscountRules +
 *                           sibling rank = existing family members + 1),
 *                           so a wizard-admitted joiner is billed the same
 *                           as a server-admitted one. Surfaced in Review.
 *   - invoice numbering   — formatInvoiceNo(settings, {year, term, seq}),
 *                           seq = invoices-for-year + 1.
 *
 * Lifecycle is NON-destructive (ApplicationStatus has no 'admitted'):
 * the application is stamped with admittedStudentId/admittedAt/admittedBy
 * and kept; ApplicationsList hides admitted applications.
 * ------------------------------------------------------------------ */
import { useMemo, useRef, useState } from 'react';
import { arrayUnion, collection, doc } from 'firebase/firestore';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Printer,
  Download,
  GraduationCap,
  Plus,
  Receipt,
  Search,
  Trash2,
  UserPlus,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { db, setDoc, addDoc, updateDoc } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  modalBackdrop as glassModalBackdrop,
  modalSurface as glassModalSurface,
  glassFieldClass,
} from '@/features/finance/ui';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { getNextAdmissionNumber } from '@/lib/admissions';
import { compareClasses } from '@/lib/classSort';
import {
  assembleInvoice,
  effectiveDiscountRules,
  fmtPula,
  formatInvoiceNo,
  formBandForForm,
  developmentFeeFor,
  DEVELOPMENT_FEE_LABEL,
  joiningInvoiceLines,
  newStructureId,
  round2,
  FEE_CATEGORY_LABELS,
  type FeeCategory,
  type FeeItem,
  type InvoiceLine,
  type Invoice,
  type OneTimeFee,
} from '@/lib/financeCore';
import { finance } from '@/features/finance/financeWrite';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { recordFinanceAudit } from '@/lib/financeAudit';
import { deliverInvoice } from '@/lib/invoiceDelivery';
import { buildInvoicePdf } from '@/lib/invoicePdf';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { auditFromContext } from '@/lib/audit';
import { currentTermKey } from '@/lib/school.config';
import type {
  AdmissionFeeReceipt,
  AdmissionsData,
  ApplicationRecord,
  FamilyPrimaryContact,
  Guardian,
  ParentContact,
  Student,
} from '@/types';

interface Props {
  app: ApplicationRecord;
  /** 0..1 — proportion of this term's tuition to bill (mid-year joiners). */
  termFeeFactor: number;
  onClose: () => void;
  onAdmitted: (studentId: string, admissionNumber: string) => void;
}

type FamilyChoice =
  | { mode: 'existing'; familyId: string; familyName: string }
  | { mode: 'new'; familyName: string };

const STEPS = ['Up-front fees', 'Class', 'Family', 'Charges', 'Review'] as const;

function primaryGuardianOf(app: ApplicationRecord): Guardian | null {
  const gs = Array.isArray(app.guardians) ? app.guardians : [];
  return gs.find((g) => g.isPrimaryContact) ?? gs[0] ?? null;
}

/**
 * Ensure the development fee is auto-applied on the joining invoice. The
 * normal prefill only pulls MANDATORY once/annual catalog fees, so a
 * development fee marked optional (or otherwise missed) wouldn't appear.
 * Here we make sure any active fee item that reads as a development fee is
 * included by default — the officer can still delete it on the Charges step.
 */
function withDevelopmentFee(lines: InvoiceLine[], feeItems: FeeItem[], band: string): InvoiceLine[] {
  const looksLikeDev = (s: string) => /develop/i.test(s);
  if (lines.some((l) => looksLikeDev(l.label))) return lines;
  const amount = round2(developmentFeeFor(band));
  if (amount <= 0) return lines;
  const dev = feeItems.find((f) => f.active !== false && looksLikeDev(f.name));
  return [
    ...lines,
    {
      feeItemId: dev?.id,
      label: dev?.name ?? DEVELOPMENT_FEE_LABEL,
      category: dev?.category ?? 'other',
      amount,
    },
  ];
}

export default function AdmitWizard({ app, termFeeFactor, onClose, onAdmitted }: Props) {
  const { userData } = useAuth();
  const { classes, families, students, classById } = useData();
  const fin = useFinanceData();

  const term = useMemo(() => currentTermKey(), []);
  const year = app.applyingForYear;
  const band = useMemo(() => formBandForForm(app.applyingForForm), [app.applyingForForm]);
  const primaryGuardian = useMemo(() => primaryGuardianOf(app), [app]);

  /* -------------------------------- step state -------------------------------- */
  const [step, setStep] = useState(0);

  // Step 0 — up-front fees (application + registration). These are collected
  // before admission and are a hard prerequisite (mirrors the server gate).
  // They are NOT re-billed on the joining invoice.
  const [feeReceipts, setFeeReceipts] = useState<{
    application?: AdmissionFeeReceipt;
    registration?: AdmissionFeeReceipt;
  }>(app.feeReceipts ?? {});
  const [feeBusy, setFeeBusy] = useState<'application' | 'registration' | null>(null);
  const feeAmounts = useMemo(
    () => ({
      application: Number(fin.settings.applicationFeeAmount) || 0,
      registration: Number(fin.settings.registrationFeeAmount) || 0,
    }),
    [fin.settings.applicationFeeAmount, fin.settings.registrationFeeAmount],
  );
  const feesOk = feeReceipts.application?.received === true && feeReceipts.registration?.received === true;

  async function confirmFee(kind: 'application' | 'registration', patch: Partial<AdmissionFeeReceipt>) {
    const existing = feeReceipts[kind] ?? { received: false };
    let merged: AdmissionFeeReceipt = { ...existing, ...patch };
    if (patch.received) {
      merged = {
        ...merged,
        received: true,
        receivedAt: merged.receivedAt ?? new Date().toISOString(),
        byName: merged.byName ?? (userData?.displayName ?? userData?.email ?? ''),
        amount: merged.amount ?? feeAmounts[kind],
      };
    }
    const next = { ...feeReceipts, [kind]: merged };
    setFeeReceipts(next); // optimistic
    setFeeBusy(kind);
    try {
      await updateDoc(doc(db, COLLECTIONS.applications, app.id), {
        feeReceipts: next,
        updatedAt: new Date().toISOString(),
      });
    } finally {
      setFeeBusy(null);
    }
  }

  // Step 1 — class (prefilled from decisions)
  const [classId, setClassId] = useState(app.decisions?.assignedClassId ?? '');

  // Step 2 — family (prefilled from familyLink)
  const [family, setFamily] = useState<FamilyChoice | null>(() => {
    const fl = app.familyLink;
    if (fl?.mode === 'existing' && fl.familyId) {
      return { mode: 'existing', familyId: fl.familyId, familyName: fl.familyName ?? '' };
    }
    if (fl?.mode === 'new') {
      return { mode: 'new', familyName: fl.familyName ?? `${app.surname} Family` };
    }
    return null;
  });
  const [familyQuery, setFamilyQuery] = useState('');
  const [newFamilyName, setNewFamilyName] = useState(
    app.familyLink?.mode === 'new' ? (app.familyLink.familyName ?? '') : '',
  );

  // Step 3 — charges (prefilled once, then editable)
  const structureLines = useMemo(() => {
    const s = fin.feeStructures.find((st) => st.id === newStructureId(year, band));
    return s?.lines ?? [];
  }, [fin.feeStructures, year, band]);

  const oneTimeFees = useMemo<OneTimeFee[]>(
    () =>
      fin.feeItems
        .filter(
          (it) =>
            it.active !== false &&
            !it.optional &&
            (it.recurrence === 'once' || it.recurrence === 'annual') &&
            it.category !== 'registration',
        )
        .map((it) => ({ feeItemId: it.id, label: it.name, category: it.category, amount: it.amount })),
    [fin.feeItems],
  );

  const [lines, setLines] = useState<InvoiceLine[] | null>(null);
  const [linesSeeded, setLinesSeeded] = useState(false);
  // Seed editable lines once finance data is ready (and not yet seeded).
  if (!linesSeeded && !fin.loading) {
    setLines(
      withDevelopmentFee(
        joiningInvoiceLines({ structureLines, oneTimeFees, termFeeFactor }),
        fin.feeItems,
        band,
      ),
    );
    setLinesSeeded(true);
  }
  const editLines = lines ?? [];

  /* --------------------------- sibling discount (mirrored) -------------------- */
  const siblingRank = useMemo(() => {
    if (family?.mode === 'existing') {
      const fam = families.find((f) => f.id === family.familyId);
      return (fam?.studentIds?.length ?? 0) + 1;
    }
    return 1; // first child of a brand-new family
  }, [family, families]);

  const effRules = useMemo(
    () => effectiveDiscountRules(fin.discountRules, fin.settings),
    [fin.discountRules, fin.settings],
  );

  // A non-persisted preview invoice drives the Review totals + discount display.
  const preview = useMemo(
    () =>
      assembleInvoice({
        id: 'preview',
        invoiceNo: 'preview',
        studentId: 'preview',
        term,
        year,
        issuedAt: new Date().toISOString(),
        dueDate: new Date().toISOString(),
        lines: editLines,
        rules: effRules,
        ctx: { siblingRank },
      }),
    [editLines, effRules, siblingRank, term, year],
  );

  /* --------------------------------- submit ----------------------------------- */
  const [submit, setSubmit] = useState<null | 'saving' | 'success' | 'error'>(null);
  const [result, setResult] = useState<AdmitResult | null>(null);
  const [submitMsg, setSubmitMsg] = useState('');

  const fullName = `${app.firstName} ${app.surname}`.trim();
  const selectedClass = classById(classId);

  const familyOk =
    family?.mode === 'existing'
      ? !!family.familyId
      : family?.mode === 'new'
        ? !!family.familyName.trim()
        : false;

  const canContinue =
    step === 0
      ? feesOk
      : step === 1
        ? !!classId
        : step === 2
          ? familyOk
          : step === 3
            ? editLines.length > 0
            : true;

  async function handleSubmit() {
    if (submit === 'saving') return;
    setSubmit('saving');
    setSubmitMsg('Creating the student record…');
    try {
      const now = new Date().toISOString();
      const actor = userData ? { id: userData.uid ?? '', email: userData.email } : null;

      // 1) Pre-mint the new student id (addDoc-style auto id, kept on the doc).
      const newId = doc(collection(db, COLLECTIONS.students)).id;
      const admissionNumber = getNextAdmissionNumber(students);

      // 2) Resolve the family. For a NEW family we create it first so its
      //    id is available for the student doc; for an existing one we
      //    arrayUnion the new student after writing it.
      let familyId: string;
      if (family?.mode === 'new') {
        const newPrimary: FamilyPrimaryContact = {
          name: primaryGuardian?.fullName || fullName,
          email: primaryGuardian?.email || undefined,
          whatsapp: primaryGuardian?.whatsapp || undefined,
          consentToSend: primaryGuardian?.consentToSend ?? false,
          guardianRef: primaryGuardian?.id
            ? { studentId: newId, guardianId: primaryGuardian.id }
            : undefined,
        };
        const famRef = await addDoc(collection(db, COLLECTIONS.families), {
          name: (family.familyName.trim() || `${app.surname} Family`),
          studentIds: [newId],
          primaryContact: newPrimary,
          createdBy: userData?.uid ?? '',
        });
        familyId = famRef.id;
      } else if (family?.mode === 'existing') {
        familyId = family.familyId;
      } else {
        throw new Error('A family must be linked or created before admitting.');
      }

      // 3) Build + write the student doc (mirrors the server clone).
      const parentContact: ParentContact | undefined = primaryGuardian
        ? {
            guardianName: primaryGuardian.fullName || '',
            email: primaryGuardian.email || '',
            whatsapp: primaryGuardian.whatsapp || '',
            consentToSend: primaryGuardian.consentToSend ?? false,
          }
        : undefined;

      const admissions: AdmissionsData = {
        firstName: app.firstName,
        surname: app.surname,
        nationality: app.nationality,
        homeLanguage: app.homeLanguage || '',
        religion: app.religion || '',
        address: app.address,
        guardians: Array.isArray(app.guardians) ? app.guardians : [],
        medical: app.medical,
        emergencyContactName: app.emergencyContactName || '',
        emergencyContactPhone: app.emergencyContactPhone || '',
        previousSchool: app.previousSchool || '',
        boardingStatus: app.boardingStatus || 'day',
        dateOfAdmission: now.slice(0, 10),
        notes: app.notes || '',
        updatedAt: now,
        updatedBy: userData?.uid ?? '',
      };

      const studentDoc: Student = {
        id: newId,
        fullName,
        currentClass: classId,
        gender: app.gender,
        dateOfBirth: app.dateOfBirth,
        admissionNumber,
        subjects: [],
        subjectTeachers: {},
        parentContact,
        admissions,
        familyId,
        createdAt: now,
        createdBy: userData?.uid ?? '',
        updatedAt: now,
        updatedBy: userData?.uid ?? '',
      };
      // setDoc wrapper strips undefined recursively (Firestore-safe).
      await setDoc(doc(db, COLLECTIONS.students, newId), studentDoc as unknown as Record<string, unknown>);

      // 4) Link an EXISTING family to the new student.
      if (family?.mode === 'existing') {
        await updateDoc(doc(db, COLLECTIONS.families, familyId), {
          studentIds: arrayUnion(newId),
          updatedAt: now,
        });
      }

      // 5) Joining invoice — canonical term id so a later billing run is idempotent.
      setSubmitMsg('Raising the joining invoice…');
      const invId = `${year}_${term}_${newId}`;
      const seq = fin.invoices.filter((i) => String(i.year) === String(year)).length + 1;
      const invoiceNo = formatInvoiceNo(fin.settings, { year, term, seq });
      const dueDate = new Date(Date.now() + (fin.settings.dueDays || 14) * 86400000).toISOString();
      const invoice = assembleInvoice({
        id: invId,
        invoiceNo,
        studentId: newId,
        studentName: fullName,
        familyId,
        term,
        year,
        issuedAt: now,
        dueDate,
        lines: editLines,
        rules: effRules,
        ctx: { siblingRank },
      });
      await finance.invoices.set(invId, invoice as unknown as Record<string, unknown>);

      // 6) Lifecycle — NON-destructive: stamp the application, keep it.
      await updateDoc(doc(db, COLLECTIONS.applications, app.id), {
        admittedStudentId: newId,
        admittedAt: now,
        admittedBy: userData?.uid ?? '',
        status: 'approved',
        updatedAt: now,
      });

      // 7) Audit (admissions + finance).
      auditFromContext(userData, 'application_admitted', 'admissions', {
        targetId: newId,
        targetType: 'student',
        targetName: fullName,
        details: {
          applicationId: app.id,
          referenceNumber: app.referenceNumber,
          admissionNumber,
          assignedClassId: classId,
          subjectCount: 0,
          familyId,
          familyMode: family?.mode,
          invoiceId: invId,
          invoiceTotal: invoice.total,
        },
      });
      await recordFinanceAudit({
        action: 'invoice.joining',
        actor,
        subjectId: invId,
        after: {
          invoiceNo,
          studentId: newId,
          familyId,
          subtotal: invoice.subtotal,
          discountTotal: invoice.discountTotal,
          total: invoice.total,
          siblingRank,
        },
      });

      // 8) Deliver the invoice — render PDF, upload, email the parent.
      //    Best-effort: the admission is already committed above, so a
      //    PDF/email hiccup (or sendInvoiceEmail not yet deployed) must
      //    never undo it. Reported honestly in the success line.
      setSubmitMsg('Emailing the invoice to the parent…');
      const delivery = await deliverInvoice(invoice, {
        toEmail: primaryGuardian?.email,
        studentName: fullName,
      });

      setSubmit('success');
      const emailNote = delivery.emailed
        ? `Invoice ${invoiceNo} emailed to ${primaryGuardian?.email}.`
        : `Invoice ${invoiceNo} raised${delivery.stored ? ' (PDF saved)' : ''}${delivery.reason ? ` — ${delivery.reason}` : ''}.`;
      setResult({
        invoice,
        admissionNumber,
        fullName,
        className: classById(classId)?.name ?? '—',
        familyLabel: family?.mode === 'new' ? `${family.familyName || `${app.surname} Family`} (new)` : (family?.familyName ?? '—'),
        emailed: delivery.emailed,
        emailNote,
      });
      setSubmitMsg(`Admitted as ${admissionNumber}. ${emailNote}`);
    } catch (e) {
      setSubmit('error');
      setSubmitMsg((e as Error).message || 'Admission failed.');
    }
  }

  /* --------------------------------- render ----------------------------------- */
  const frozen = submit === 'saving' || submit === 'success';
  // Only a click whose press AND release are on the backdrop itself dismisses
  // the modal. This stops a text-selection drag that starts inside an input and
  // releases on the backdrop (which otherwise fires a click on the backdrop and
  // closed the modal mid-edit).
  const pressedOnBackdrop = useRef(false);

  return (
    <div
      className={glassModalBackdrop}
      onMouseDown={(e) => { pressedOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        const onBackdrop = pressedOnBackdrop.current && e.target === e.currentTarget;
        pressedOnBackdrop.current = false;
        if (!frozen && onBackdrop) onClose();
      }}
    >
      <div
        className={glassModalSurface('w-full max-w-5xl max-h-[92vh] flex flex-col')}
        onClick={(e) => e.stopPropagation()}
      >
        {/* header + stepper */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100/80 dark:border-gray-700/60">
          <div className="flex items-center justify-between mb-4">
            <h2 className="flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-100">
              <GraduationCap size={18} className="text-primaryLight" />
              Admit {fullName}
              <span className="font-mono text-xs text-gray-400">{app.referenceNumber}</span>
            </h2>
            <button
              onClick={onClose}
              disabled={frozen}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40"
            >
              <X size={18} />
            </button>
          </div>
          <Stepper step={step} />
        </div>

        {/* body */}
        <div className="px-6 py-5 overflow-y-auto">
          {result ? (
            <AdmitSummary result={result} onDone={() => onAdmitted(result.invoice.studentId, result.admissionNumber)} />
          ) : (<>
          {step === 0 && (
            <StepFees
              loading={fin.loading}
              amounts={feeAmounts}
              receipts={feeReceipts}
              busy={feeBusy}
              onConfirm={confirmFee}
            />
          )}

          {step === 1 && (
            <StepClass
              classes={classes}
              applyingForForm={app.applyingForForm}
              classId={classId}
              setClassId={setClassId}
            />
          )}

          {step === 2 && (
            <StepFamily
              app={app}
              families={families}
              students={students}
              classNameOf={(id) => classById(id)?.name ?? '—'}
              family={family}
              setFamily={setFamily}
              query={familyQuery}
              setQuery={setFamilyQuery}
              newFamilyName={newFamilyName}
              setNewFamilyName={setNewFamilyName}
            />
          )}

          {step === 3 && (
            <StepCharges
              loading={fin.loading}
              hasStructure={structureLines.length > 0}
              year={year}
              band={band}
              termFeeFactor={termFeeFactor}
              lines={editLines}
              setLines={(next) => setLines(next)}
            />
          )}

          {step === 4 && (
            <StepReview
              fullName={fullName}
              gender={app.gender}
              className={selectedClass?.name ?? '—'}
              family={family}
              siblingRank={siblingRank}
              preview={preview}
            />
          )}
          </>)}
        </div>

        {/* footer */}
        {!result && (
        <div className="px-6 py-4 border-t border-gray-100/80 dark:border-gray-700/60 flex items-center justify-between gap-3">
          <button
            onClick={() => (step === 0 ? onClose() : setStep((s) => s - 1))}
            disabled={frozen}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-40"
          >
            <ArrowLeft size={15} />
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => canContinue && setStep((s) => s + 1)}
              disabled={!canContinue}
              className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-primaryLight to-primary hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
              <ArrowRight size={15} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={frozen || !feesOk || !classId || !familyOk || editLines.length === 0}
              className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-primaryLight to-primary hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus size={15} />
              Admit &amp; raise invoice
            </button>
          )}
        </div>
        )}
      </div>

      <SaveConfirmation
        show={submit === 'saving' || submit === 'error'}
        status={submit === 'error' ? 'error' : 'saving'}
        message={submitMsg}
        autoDismissMs={0}
        onDismiss={() => setSubmit(null)}
      />
    </div>
  );
}

/* ================================ stepper ================================ */
function Stepper({ step }: { step: number }) {
  return (
    <ol className="flex items-center gap-2">
      {STEPS.map((label, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <li key={label} className="flex items-center gap-2 flex-1 last:flex-none">
            <span
              className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0 ${
                done
                  ? 'bg-emerald-500 text-white'
                  : active
                    ? 'bg-primaryLight text-white'
                    : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              {done ? <Check size={13} /> : i + 1}
            </span>
            <span
              className={`text-xs whitespace-nowrap ${
                active ? 'font-semibold text-gray-800 dark:text-gray-100' : 'text-gray-400'
              }`}
            >
              {label}
            </span>
            {i < STEPS.length - 1 && (
              <span className="hidden sm:block h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* =========================== step 0: up-front fees =========================== */
function StepFees({
  loading,
  amounts,
  receipts,
  busy,
  onConfirm,
}: {
  loading: boolean;
  amounts: { application: number; registration: number };
  receipts: { application?: AdmissionFeeReceipt; registration?: AdmissionFeeReceipt };
  busy: 'application' | 'registration' | null;
  onConfirm: (kind: 'application' | 'registration', patch: Partial<AdmissionFeeReceipt>) => void;
}) {
  const rows: { kind: 'application' | 'registration'; label: string }[] = [
    { kind: 'application', label: 'Application fee' },
    { kind: 'registration', label: 'Registration fee' },
  ];
  const bothOk = receipts.application?.received === true && receipts.registration?.received === true;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-xl bg-primaryLight/5 border border-primaryLight/20 p-3 text-xs text-gray-600 dark:text-gray-300">
        <Wallet size={14} className="mt-0.5 shrink-0 text-primaryLight" />
        <span>
          The one-time <strong>application</strong> and <strong>registration</strong> fees are collected
          up front and must be received before a student can be admitted. Confirm both below — they are
          not re-billed on the joining invoice.
        </span>
      </div>

      <div className="space-y-3">
        {rows.map((row) => {
          const rx = receipts[row.kind];
          const ok = rx?.received === true;
          const amount = amounts[row.kind];
          return (
            <div
              key={row.kind}
              className={`rounded-xl border p-3.5 ${
                ok
                  ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-900/15'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {ok ? (
                    <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{row.label}</p>
                    <p className="text-xs text-gray-500">
                      {amount > 0 ? fmtPula(amount) : 'Amount not set in finance settings'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => onConfirm(row.kind, { received: !ok })}
                  disabled={busy === row.kind || loading}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
                    ok
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200'
                      : 'bg-primary text-white hover:bg-primaryLight'
                  }`}
                >
                  {busy === row.kind ? 'Saving…' : ok ? 'Mark unpaid' : 'Confirm received'}
                </button>
              </div>

              {ok && (
                <div className="mt-2.5 pl-6 space-y-1.5">
                  <input
                    value={rx?.reference ?? ''}
                    onChange={(e) => onConfirm(row.kind, { received: true, reference: e.target.value })}
                    placeholder="Receipt / reference no. (optional)"
                    className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-primaryLight"
                  />
                  {(rx?.byName || rx?.receivedAt) && (
                    <p className="text-[11px] text-gray-400">
                      Confirmed{rx?.byName ? ` by ${rx.byName}` : ''}
                      {rx?.receivedAt ? ` · ${rx.receivedAt.slice(0, 10)}` : ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!bothOk && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Both fees must be confirmed received before you can continue.
        </p>
      )}
    </div>
  );
}

/* =========================== step 1: class =========================== */
function StepClass({
  classes,
  applyingForForm,
  classId,
  setClassId,
}: {
  classes: ReturnType<typeof useData>['classes'];
  applyingForForm: string;
  classId: string;
  setClassId: (id: string) => void;
}) {
  const eligible = useMemo(
    () =>
      [...classes].sort((a, b) => {
        const am = a.form === applyingForForm ? 0 : 1;
        const bm = b.form === applyingForForm ? 0 : 1;
        if (am !== bm) return am - bm;
        return compareClasses(a, b);
      }),
    [classes, applyingForForm],
  );

  return (
    <div className="space-y-5">
      <div>
        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
          <BookOpen size={13} className="text-gray-400" /> Assign to class
        </label>
        <select value={classId} onChange={(e) => setClassId(e.target.value)} className={glassFieldClass('text-sm')}>
          <option value="">Select a class…</option>
          {eligible.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.form === applyingForForm ? '  ✓ (applied form)' : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-2">
          Subjects and their teachers are set later on the pupil's profile (Subjects tab).
        </p>
      </div>
    </div>
  );
}

/* =============================== step 2: family =============================== */
function StepFamily({
  app,
  families,
  students,
  classNameOf,
  family,
  setFamily,
  query,
  setQuery,
  newFamilyName,
  setNewFamilyName,
}: {
  app: ApplicationRecord;
  families: ReturnType<typeof useData>['families'];
  students: Student[];
  classNameOf: (classId: string) => string;
  family: FamilyChoice | null;
  setFamily: (f: FamilyChoice | null) => void;
  query: string;
  setQuery: (q: string) => void;
  newFamilyName: string;
  setNewFamilyName: (n: string) => void;
}) {
  const guardianEmail = (primaryGuardianOf(app)?.email ?? '').trim().toLowerCase();

  const suggested = useMemo(() => {
    if (!guardianEmail) return [];
    return families.filter((f) => (f.primaryContact?.email ?? '').trim().toLowerCase() === guardianEmail);
  }, [families, guardianEmail]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return families
      .filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          (f.primaryContact?.name ?? '').toLowerCase().includes(q) ||
          (f.primaryContact?.email ?? '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [families, query]);

  const childrenOf = (familyId: string) =>
    students.filter((s) => s.familyId === familyId);

  // Reveal results only on intent (a query), mirroring the StaffPicker pattern.
  const showResults = !family && query.trim() !== '';

  if (family) {
    const fam = family.mode === 'existing' ? families.find((f) => f.id === family.familyId) : null;
    const kids = fam ? childrenOf(fam.id) : [];
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-primaryLight/30 bg-primaryLight/5 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-primaryLight font-semibold mb-1">
                {family.mode === 'new' ? 'New family (created on admit)' : 'Linked family'}
              </p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {family.mode === 'new' ? (family.familyName || `${app.surname} Family`) : family.familyName}
              </p>
              {fam?.primaryContact && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                  {fam.primaryContact.name}
                  {fam.primaryContact.email ? ` · ${fam.primaryContact.email}` : ''}
                  {fam.primaryContact.whatsapp ? ` · ${fam.primaryContact.whatsapp}` : ''}
                </p>
              )}
              {family.mode === 'new' && (
                <p className="text-sm text-gray-500 mt-0.5">
                  Primary contact: {primaryGuardianOf(app)?.fullName || '—'} — {app.firstName} will be the first child.
                </p>
              )}
              {kids.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Existing children: {kids.map((k) => `${k.fullName} (${classNameOf(k.currentClass)})`).join(', ')}
                </p>
              )}
            </div>
            <button
              onClick={() => setFamily(null)}
              className="text-xs text-primaryLight hover:underline shrink-0"
            >
              Change
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {suggested.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1.5">Suggested (matching the guardian's email):</p>
          <div className="space-y-2">
            {suggested.map((f) => (
              <FamilyCandidate
                key={f.id}
                name={f.name}
                contact={f.primaryContact}
                children={childrenOf(f.id)}
                classNameOf={classNameOf}
                onPick={() => setFamily({ mode: 'existing', familyId: f.id, familyName: f.name })}
              />
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-gray-700 dark:text-gray-200">Search an existing family</label>
        <div className="relative mt-1.5">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Family or contact name, email…"
            className={glassFieldClass('py-2.5 pl-9 pr-9 text-sm')}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {showResults && (
          <div className="mt-2 space-y-2">
            {results.length === 0 ? (
              <p className="text-xs text-gray-400 px-1 py-2">No families match “{query}”.</p>
            ) : (
              results.map((f) => (
                <FamilyCandidate
                  key={f.id}
                  name={f.name}
                  contact={f.primaryContact}
                  children={childrenOf(f.id)}
                  classNameOf={classNameOf}
                  onPick={() => {
                    setFamily({ mode: 'existing', familyId: f.id, familyName: f.name });
                    setQuery('');
                  }}
                />
              ))
            )}
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
        <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
          <UserPlus size={13} /> First child of a new family? Create one:
        </p>
        <div className="flex gap-2">
          <input
            value={newFamilyName}
            onChange={(e) => setNewFamilyName(e.target.value)}
            placeholder={`${app.surname} Family`}
            className={glassFieldClass('py-2 text-sm flex-1')}
          />
          <button
            onClick={() => setFamily({ mode: 'new', familyName: newFamilyName.trim() || `${app.surname} Family` })}
            className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primaryLight shrink-0"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function FamilyCandidate({
  name,
  contact,
  children,
  classNameOf,
  onPick,
}: {
  name: string;
  contact?: FamilyPrimaryContact;
  children: Student[];
  classNameOf: (classId: string) => string;
  onPick: () => void;
}) {
  return (
    <button
      onClick={onPick}
      className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primaryLight/50 hover:bg-primaryLight/5 transition-colors p-3"
    >
      <div className="flex items-center gap-2 mb-0.5">
        <Users size={14} className="text-primaryLight shrink-0" />
        <span className="font-semibold text-gray-900 dark:text-white">{name}</span>
      </div>
      {contact && (
        <p className="text-xs text-gray-600 dark:text-gray-300 ml-6">
          {contact.name}
          {contact.email ? ` · ${contact.email}` : ''}
          {contact.whatsapp ? ` · ${contact.whatsapp}` : ''}
        </p>
      )}
      <p className="text-xs text-gray-500 ml-6 mt-0.5">
        {children.length === 0
          ? 'No children on record yet'
          : `Children: ${children.map((c) => `${c.fullName} (${classNameOf(c.currentClass)})`).join(', ')}`}
      </p>
    </button>
  );
}

/* =============================== step 3: charges =============================== */
function StepCharges({
  loading,
  hasStructure,
  year,
  band,
  termFeeFactor,
  lines,
  setLines,
}: {
  loading: boolean;
  hasStructure: boolean;
  year: string;
  band: string;
  termFeeFactor: number;
  lines: InvoiceLine[];
  setLines: (next: InvoiceLine[]) => void;
}) {
  const subtotal = round2(lines.reduce((a, l) => a + (l.amount || 0), 0));
  const categories = Object.keys(FEE_CATEGORY_LABELS) as FeeCategory[];

  function update(i: number, patch: Partial<InvoiceLine>) {
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function remove(i: number) {
    setLines(lines.filter((_, idx) => idx !== i));
  }
  function add() {
    setLines([...lines, { label: 'New charge', category: 'other', amount: 0 }]);
  }

  if (loading) {
    return <p className="text-sm text-gray-500 py-6 text-center">Loading fee structure…</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {hasStructure
            ? `Prefilled from the ${year} ${band} fee structure${termFeeFactor < 1 ? ` at ${Math.round(termFeeFactor * 100)}% of this term's tuition` : ''} plus mandatory one-time fees. Edit freely.`
            : `No ${year} ${band} fee structure found — add the joining charges manually.`}
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="grid grid-cols-[1fr_8.5rem_7rem_2rem] gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/60 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <span>Description</span>
          <span>Category</span>
          <span className="text-right">Amount (P)</span>
          <span />
        </div>
        {lines.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-4 text-center">No charges. Add at least one line.</p>
        ) : (
          lines.map((l, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_8.5rem_7rem_2rem] gap-2 px-3 py-2 items-center border-t border-gray-100 dark:border-gray-700/60"
            >
              <input
                value={l.label}
                onChange={(e) => update(i, { label: e.target.value })}
                className="px-2 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-primaryLight"
              />
              <select
                value={l.category}
                onChange={(e) => update(i, { category: e.target.value as FeeCategory })}
                className="px-2 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-primaryLight"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {FEE_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={0}
                step="0.01"
                value={l.amount}
                onChange={(e) => update(i, { amount: round2(Number(e.target.value) || 0) })}
                className="px-2 py-1.5 text-sm text-right rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-primaryLight"
              />
              <button
                onClick={() => remove(i)}
                title="Remove line"
                className="flex items-center justify-center text-gray-400 hover:text-red-500"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 dark:border-gray-700/60">
          <button onClick={add} className="flex items-center gap-1.5 text-xs font-medium text-primaryLight hover:underline">
            <Plus size={13} /> Add line
          </button>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Subtotal: {fmtPula(subtotal)}
          </span>
        </div>
      </div>
      <p className="text-[11px] text-gray-400">
        Application &amp; registration fees were collected up front and are not re-billed here.
      </p>
    </div>
  );
}

/* =============================== step 4: review =============================== */
function StepReview({
  fullName,
  gender,
  className,
  family,
  siblingRank,
  preview,
}: {
  fullName: string;
  gender: 'M' | 'F';
  className: string;
  family: FamilyChoice | null;
  siblingRank: number;
  preview: ReturnType<typeof assembleInvoice>;
}) {
  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
          <GraduationCap size={15} className="text-primaryLight" /> Student
        </h3>
        <dl className="text-sm space-y-1">
          <Row k="Name" v={fullName} />
          <Row k="Gender" v={gender === 'M' ? 'Male' : 'Female'} />
          <Row k="Class" v={className} />
          <Row
            k="Family"
            v={
              family?.mode === 'new'
                ? `${family.familyName} (new — first child)`
                : family?.mode === 'existing'
                  ? `${family.familyName} (joiner is child #${siblingRank})`
                  : '—'
            }
          />
        </dl>
        <p className="text-[11px] text-gray-400 mt-2">Admission number is assigned automatically on submit.</p>
      </section>

      <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
          <Receipt size={15} className="text-primaryLight" /> Joining invoice
        </h3>
        <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {preview.lines.map((l, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{l.label}</span>
              <span className="text-gray-900 dark:text-white tabular-nums">{fmtPula(l.amount)}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1 text-sm">
          <div className="flex items-center justify-between text-gray-600 dark:text-gray-300">
            <span>Subtotal</span>
            <span className="tabular-nums">{fmtPula(preview.subtotal)}</span>
          </div>
          {preview.discounts.map((d, i) => (
            <div key={i} className="flex items-center justify-between text-emerald-600">
              <span>{d.label}</span>
              <span className="tabular-nums">− {fmtPula(d.amount)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between font-semibold text-gray-900 dark:text-white pt-1 border-t border-gray-100 dark:border-gray-700/60">
            <span>Total due</span>
            <span className="tabular-nums">{fmtPula(preview.total)}</span>
          </div>
        </div>
      </section>

      <div className="flex items-start gap-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3 text-xs text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
        <span>
          Submitting creates the student record, links the family, raises this invoice, and marks the
          application as admitted (it stays on file for the audit trail).
        </span>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] gap-2">
      <dt className="text-gray-500">{k}</dt>
      <dd className="text-gray-900 dark:text-white">{v}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Admission summary — shown after a successful admit instead of
 * bouncing straight back to the list. Lets the officer print or
 * download the joining invoice for verification before finishing.
 * ------------------------------------------------------------------ */
type AdmitResult = {
  invoice: Invoice;
  admissionNumber: string;
  fullName: string;
  className: string;
  familyLabel: string;
  emailed: boolean;
  emailNote: string;
};

function AdmitSummary({ result, onDone }: { result: AdmitResult; onDone: () => void }) {
  const { invoice } = result;

  async function getPdf() {
    const logo = await loadLogoDataUrl().catch(() => null);
    return buildInvoicePdf(invoice, logo ? { logoDataUrl: logo } : {});
  }
  async function printInvoice() {
    try {
      const url = (await getPdf()).output('bloburl') as unknown as string;
      window.open(url, '_blank');
    } catch { /* ignore */ }
  }
  async function downloadInvoice() {
    try {
      (await getPdf()).save(`${invoice.invoiceNo || invoice.id}.pdf`);
    } catch { /* ignore */ }
  }

  return (
    <div className="max-w-xl mx-auto py-2">
      <div className="flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
          <CheckCircle2 size={30} className="text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{result.fullName} admitted</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Admission number {result.admissionNumber}</p>
      </div>

      <div className="mt-5 rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/60 text-sm">
        <SummaryRow k="Class" v={result.className} />
        <SummaryRow k="Family" v={result.familyLabel} />
        <SummaryRow k="Invoice" v={invoice.invoiceNo} />
        <SummaryRow k="Invoice total" v={fmtPula(invoice.total)} strong />
      </div>

      <p className="mt-3 text-xs text-gray-400 text-center">{result.emailNote}</p>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        <button
          onClick={printInvoice}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Printer size={15} /> Print invoice
        </button>
        <button
          onClick={downloadInvoice}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Download size={15} /> Download PDF
        </button>
        <button
          onClick={onDone}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-primaryLight to-primary hover:shadow-lg transition-all"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function SummaryRow({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-gray-500 dark:text-gray-400">{k}</span>
      <span className={`tabular-nums ${strong ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'}`}>{v}</span>
    </div>
  );
}
